import React, { useState, useEffect } from 'react';
import { TextField, Grid, Checkbox, FormControlLabel, Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { useParams, useHistory } from 'react-router-dom';
import axios from '../../utils/axios.utils';

interface RouteParams {
  liquidId: string;
}

interface LiquidType {
  id: string;
  name: string;
  description: string;
}

interface LiquidData {
  id?: string;
  global_mfr_id: string;
  liquid_name: string;
  liquid_name_desc: string;
  liquid_type_id: string;
  liquid_type?: LiquidType;
  liquid_rate_type_id: string;
  liquid_rate: string;
  liquid_rate_unit: string;
  epa_number: string;
  liquid_density: string;
  min_rate: string;
  max_rate: string;
  target_rate: string;
  liquid_density_tol: string;
  adjustable: boolean;
  adjust_by: string;
  locked: boolean;
  active: boolean;
}

interface ApiResponse<T> {
  data: T;
}

interface LiquidListResponse extends ApiResponse<LiquidData[]> {}
interface LiquidResponse extends ApiResponse<LiquidData> {}

interface EditMfrLiquidFormProps {
  id: string;
  onSuccess: () => void;
  initialData?: any;
  showSnackbar?: (message: string, severity: 'success' | 'error') => void;
}

const EditMfrLiquidForm: React.FC<EditMfrLiquidFormProps> = ({ id, onSuccess, initialData, showSnackbar = () => {} }) => {
  const history = useHistory();
  const [formValues, setFormValues] = useState({
    global_mfr_id: '',
    liquid_name: '',
    liquid_name_desc: '',  // This is the backend field
    description: '',      // This is the form field that maps to liquid_name_desc
    mfr_product_id: '',
    liquid_type_id: '',
    epa_number: '',
    liquid_density: '',
    min_rate: '',
    max_rate: '',
    target_rate: '',
    liquid_density_tol: '',
    adjustable: false,
    adjust_by: '',
    locked: false,
    active: true,
    // Map initial data with backward compatibility
    ...(initialData || {})
  });

  const [liquidTypes, setLiquidTypes] = useState<any[]>([]);
  const [globalMfrs, setGlobalMfrs] = useState<any[]>([]);

  // Remove the initialData useEffect as we're handling initial data in the main fetchData effect
  // This was causing the form to reset when initialData changed

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch manufacturers and liquid types in parallel using axios
        const [mfrResponse, liquidTypesResponse] = await Promise.all([
          axios.get<any, any>('api/admin/autotreat_mfr/mfrs/', {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          }),
          axios.get<any, any>('api/admin/autotreat_mfr/liquid_types/', {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          }),
        ]);

        setGlobalMfrs(mfrResponse.data);
        setLiquidTypes(liquidTypesResponse.data);

        // If in edit mode, fetch the liquid data
        if (id) {
          const response = await axios.get<any, any>(`api/admin/autotreat_mfr/mfr_liquids/${id}/`, {
            headers: { Authorization: `Bearer ${sessionStorage.getItem('accessToken')}` },
          });
          
          // Extract and transform the data we need
          const { liquid_type, description, liquid_name_desc, ...restData } = response.data;
          
          // Get the description from either the description field or liquid_name_desc (for backward compatibility)
          const descValue = description || liquid_name_desc || '';
          
          // Explicitly set only the fields we want in the form state
          const formData = {
            global_mfr_id: response.data.global_mfr_id || '',
            liquid_name: response.data.liquid_name || '',
            mfr_product_id: response.data.mfr_product_id || '',
            epa_number: response.data.epa_number || '',
            liquid_density: response.data.liquid_density || '',
            min_rate: response.data.min_rate || '',
            max_rate: response.data.max_rate || '',
            target_rate: response.data.target_rate || '',
            liquid_density_tol: response.data.liquid_density_tol || '',
            adjustable: response.data.adjustable || false,
            adjust_by: response.data.adjust_by || '',
            locked: response.data.locked || false,
            active: response.data.active !== undefined ? response.data.active : true,
            // Use liquid_type.id if available, otherwise use liquid_type_id from response
            liquid_type_id: (liquid_type?.id || response.data.liquid_type_id || '').toString(),
            liquid_name_desc: descValue,
            description: descValue  // This is the form field that maps to liquid_name_desc
          };
          
          setFormValues(formData);
        }
      } catch (error: any) {
        console.error('Error fetching data:', error);
        const errorMessage = error?.response?.data?.message || 
          (error instanceof Error ? error.message : 'An error occurred while fetching data');
        showSnackbar(errorMessage, 'error');
      }
    };

    fetchData();
  }, [id]);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | SelectChangeEvent<unknown>) => {
    const target = event.target as any; // Using 'as any' for pragmatic type handling across MUI components
    const name = target.name as string;
    let valueToSet: any;
    
    console.log(`Field changed - Name: ${name}, Value: ${target.value}`, { target }); // Debug log

    if (target.type === 'checkbox' && typeof target.checked === 'boolean') {
      valueToSet = target.checked;
    } else {
      valueToSet = target.value;
    }

    if (name) {
      setFormValues(prev => ({ ...prev, [name]: valueToSet }));
    }
  };

  // Removed handleSelectChange as we're using handleInputChange for all inputs

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const url = id 
        ? `api/admin/autotreat_mfr/mfr_liquids/${id}/`
        : 'api/admin/autotreat_mfr/mfr_liquids/';
      const method = id ? 'put' : 'post';
      
      // Prepare the data to send, ensuring we only send valid values and use the correct field names
      const dataToSend: any = {};
      
      // Map form fields to API fields
      const fieldMappings = {
        global_liquid_id: 'global_liquid_id',
        global_mfr_id: 'global_mfr_id',
        liquid_name: 'liquid_name',
        description: 'liquid_name_desc',  // Map form field 'description' to backend field 'liquid_name_desc'
        mfr_product_id: 'mfr_product_id',
        liquid_type_id: 'liquid_type',
        epa_number: 'epa_number',
        liquid_density: 'liquid_density',
        min_rate: 'min_rate',
        max_rate: 'max_rate',
        target_rate: 'target_rate',
        liquid_density_tol: 'liquid_density_tol',
        adjustable: 'adjustable',
        adjust_by: 'adjust_by',
        locked: 'locked',
        active: 'active'
      };

      // Map and clean the data
      Object.entries(fieldMappings).forEach(([formField, apiField]) => {
        if (formValues[formField] !== undefined && formValues[formField] !== null && formValues[formField] !== '') {
          dataToSend[apiField] = formValues[formField];
        }
      });
      
      console.log('Submitting data:', dataToSend); // Debug log
      
      const response = await axios[method](url, dataToSend, {
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}`
        }
      });
      
      console.log('API Response:', response); // Debug log
      
      if (response.status >= 200 && response.status < 300) {
        showSnackbar('Treatment product updated successfully!', 'success');
        onSuccess();
      } else {
        throw new Error(response.data?.message || 'Failed to save treatment product');
      }
    } catch (error: any) {
      console.error('Error saving liquid:', error);
      const errorMessage = error.response?.data?.message || 
                         error.message || 
                         'Failed to save liquid. Please check the console for more details.';
      showSnackbar(errorMessage, 'error');
    }
  };

  const handleDelete = async () => {
    if (!window.confirm('Are you sure you want to delete this treatment product?') || !id) return;
    try {
      await axios.delete<any, any>(`api/admin/autotreat_mfr/mfr_liquids/${id}/`, {
        headers: { 
          'Authorization': `Bearer ${sessionStorage.getItem('accessToken')}` 
        }
      });
      
      onSuccess();
      showSnackbar('Treatment product deleted successfully', 'success');
    } catch (error: any) {
      console.error('Error deleting liquid:', error);
      const errorMessage = error?.response?.data?.message || 
        (error instanceof Error ? error.message : 'Failed to delete treatment product. Please try again.');
      showSnackbar(errorMessage, 'error');
    }
  };

  return (
    <form id="dialog-form" onSubmit={handleSubmit}>
      <Grid container spacing={3}>
        {/* Global MFR ID Dropdown */}
        <Grid item xs={12} sm={6}>
          <Select
            name="global_mfr_id"
            value={formValues.global_mfr_id}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            displayEmpty
            required
          >
            <MenuItem value="" disabled>
              Select Global MFR ID
            </MenuItem>
            {globalMfrs.map((mfr) => (
              <MenuItem key={mfr.global_mfr_id} value={mfr.global_mfr_id}>
                {mfr.mfr_name}
              </MenuItem>
            ))}
          </Select>
        </Grid>

        {/* Liquid Type Dropdown */}
        <Grid item xs={12} sm={6}>
          <Select
            name="liquid_type_id"
            value={formValues.liquid_type_id}
            onChange={handleInputChange}
            fullWidth
            variant="outlined"
            displayEmpty
            required
          >
            <MenuItem value="" disabled>
              Select Liquid Type
            </MenuItem>
            {liquidTypes.map((type) => (
              <MenuItem key={type.liquid_type_id} value={type.liquid_type_id}>
                {type.liquid_type}
              </MenuItem>
            ))}
          </Select>
        </Grid>

{/* Liquid Name */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="liquid_name"
            label="Liquid Name"
            fullWidth
            variant="outlined"
            value={formValues.liquid_name}
            onChange={handleInputChange}
            required
          />
        </Grid>

        {/* Liquid Description */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="description"
            label="Liquid Description"
            fullWidth
            variant="outlined"
            value={formValues.description}
            onChange={handleInputChange}
          />
        </Grid>

        {/* EPA Number */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="epa_number"
            label="EPA Number"
            fullWidth
            variant="outlined"
            value={formValues.epa_number}
            onChange={handleInputChange}
          />
        </Grid>

        {/* MFR Product ID */}
        <Grid item xs={12} sm={6}>
          <TextField
            name="mfr_product_id"
            label="MFR Product ID"
            fullWidth
            variant="outlined"
            value={formValues.mfr_product_id}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Liquid Density */}
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="liquid_density"
            label="Liquid Density"
            fullWidth
            variant="outlined"
            value={formValues.liquid_density}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Min Rate */}
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="min_rate"
            label="Min Rate"
            fullWidth
            variant="outlined"
            value={formValues.min_rate}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Max Rate */}
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="max_rate"
            label="Max Rate"
            fullWidth
            variant="outlined"
            value={formValues.max_rate}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Target Rate */}
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="target_rate"
            label="Target Rate"
            fullWidth
            variant="outlined"
            value={formValues.target_rate}
            onChange={handleInputChange}
          />
        </Grid>

        {/* Liquid Density TOL */}
        <Grid item xs={12} sm={6}>
          <TextField
            type="number"
            name="liquid_density_tol"
            label="Liquid Density TOL"
            fullWidth
            variant="outlined"
            value={formValues.liquid_density_tol}
            onChange={handleInputChange}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControlLabel 
            control={
              <Checkbox 
                name="adjustable" 
                checked={formValues.adjustable} 
                onChange={handleInputChange} 
              />
            } 
            label="Adjustable" 
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            name="adjust_by"
            label="Adjust By"
            type="number"
            fullWidth
            variant="outlined"
            value={formValues.adjust_by}
            onChange={handleInputChange}
            disabled={!formValues.adjustable}
            InputLabelProps={{
              shrink: formValues.adjustable || formValues.adjust_by ? true : undefined,
            }}
          />
        </Grid>
        <Grid item xs={12} container spacing={2} sx={{ display: 'flex', flexWrap: 'wrap' }}>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox 
                  name="locked" 
                  checked={formValues.locked || false} 
                  onChange={handleInputChange} 
                />
              }
              label="Locked"
            />
          </Grid>
          <Grid item xs={6}>
            <FormControlLabel
              control={
                <Checkbox 
                  name="active" 
                  checked={formValues.active || false} 
                  onChange={handleInputChange} 
                />
              }
              label="Active"
            />
          </Grid>
        </Grid>
      </Grid>
    </form>
  );
};

export default EditMfrLiquidForm;